/**
 * Dialogue de confirmation réutilisable (remplace window.confirm).
 *
 * Usage :
 *   const confirm = useConfirm();
 *   if (await confirm({ title: "Supprimer ?", message: "…", danger: true })) { … }
 */
import { createContext, useCallback, useContext, useRef, useState } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";

const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
    const [state, setState] = useState(null); // { title, message, confirmLabel, danger }
    const resolver = useRef(null);

    const confirm = useCallback((opts = {}) => {
        setState({
            title: opts.title || "Confirmer",
            message: opts.message || "",
            confirmLabel: opts.confirmLabel || "Confirmer",
            cancelLabel: opts.cancelLabel || "Annuler",
            danger: opts.danger !== false, // par défaut style danger
        });
        return new Promise((resolve) => { resolver.current = resolve; });
    }, []);

    const close = (result) => {
        setState(null);
        resolver.current?.(result);
        resolver.current = null;
    };

    return (
        <ConfirmContext.Provider value={confirm}>
            {children}
            {state && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/50" onClick={() => close(false)} />
                    <div className="relative w-full max-w-md rounded-2xl bg-card border border-border shadow-2xl p-6">
                        <div className="flex items-start gap-3">
                            <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${state.danger ? "bg-destructive/10 text-destructive" : "bg-primary/10 text-primary"}`}>
                                <AlertTriangle className="h-5 w-5" />
                            </div>
                            <div className="min-w-0">
                                <h3 className="font-display text-lg font-bold text-foreground">{state.title}</h3>
                                {state.message && <p className="text-sm text-muted-foreground mt-1.5">{state.message}</p>}
                            </div>
                        </div>
                        <div className="mt-6 flex justify-end gap-2">
                            <button
                                onClick={() => close(false)}
                                className="rounded-full px-4 py-2 text-sm font-semibold border border-border hover:bg-secondary"
                            >
                                {state.cancelLabel}
                            </button>
                            <button
                                autoFocus
                                onClick={() => close(true)}
                                className={`rounded-full px-4 py-2 text-sm font-semibold text-white hover:opacity-90 ${state.danger ? "bg-destructive" : "bg-primary"}`}
                            >
                                {state.confirmLabel}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </ConfirmContext.Provider>
    );
}

export function useConfirm() {
    const ctx = useContext(ConfirmContext);
    // Repli sur window.confirm si le provider n'est pas monté (sécurité)
    return ctx || (({ message, title } = {}) => Promise.resolve(window.confirm(message || title || "Confirmer ?")));
}
